import dayjs from "dayjs";
import { DBSchema, IDBPDatabase, openDB } from "idb";

import { parseUploadedUrl } from "@/Services/UploadPhoto";

import {
    StoredPhoto,
    StoredPhotoMeta,
    StoredPhotoSlot,
} from "../PhotoStorage.types";
import { ReadyGate } from "./readyGate";

interface DatabaseSchema extends DBSchema {
    photos: {
        key: string;
        value: StoredPhoto;
        indexes: { "by-date": string };
    };
    photoSlots: {
        key: string;
        value: StoredPhotoSlot;
        indexes: { "by-market": string };
    };
}

const DB_NAME = "PhotoGalleryDB";
const DB_VERSION = 2;
const PHOTO_STORE = "photos";
const SLOTS_STORE = "photoSlots";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true for any IDB connection error that warrants a reconnect retry.
 * Covers standard DOMException codes + iOS Safari "UnknownError: Connection
 * to Indexed Database server lost" that fires on low battery / memory pressure.
 */
const isIdbConnectionError = (e: unknown): boolean => {
    if (!(e instanceof Error)) return false;

    const name = e.name ?? "";
    const msg = e.message ?? "";

    return (
        name === "InvalidStateError" ||
        name === "TransactionInactiveError" ||
        // iOS Safari kills IDB under memory/battery pressure
        (name === "UnknownError" &&
            /connection to indexed database server lost/i.test(msg)) ||
        /connection is closing/i.test(msg) ||
        /database.*clos/i.test(msg) ||
        /internal error/i.test(msg)
    );
};

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class PhotoStorageDb {
    private db: IDBPDatabase<DatabaseSchema> | null = null;
    private initPromise: Promise<void> | null = null;
    private readonly gate = new ReadyGate(true);

    async init(): Promise<void> {
        if (this.db) {
            try {
                // Accessing objectStoreNames throws if the connection is dead.
                this.db.objectStoreNames;
                return;
            } catch {
                this.db = null;
                this.initPromise = null;
            }
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this.openDatabase();
        return this.initPromise;
    }

    /** Resolves when the DB connection is ready. */
    waitUntilReady(): Promise<void> {
        return this.gate.wait();
    }

    private resetConnection(): void {
        // IDB process was killed by the OS (common on iOS low battery).
        // Reset state so the next init() call reopens the connection.
        console.warn(
            "IDB connection terminated by the OS — will reconnect on next operation",
        );
        this.db = null;
        this.initPromise = null;
        this.gate.close();
    }

    private async openDatabase(): Promise<void> {
        try {
            this.db = await openDB<DatabaseSchema>(DB_NAME, DB_VERSION, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains(PHOTO_STORE)) {
                        const store = db.createObjectStore(PHOTO_STORE, {
                            keyPath: "id",
                        });
                        store.createIndex("by-date", "date");
                    }

                    if (!db.objectStoreNames.contains(SLOTS_STORE)) {
                        const store = db.createObjectStore(SLOTS_STORE, {
                            keyPath: "url",
                        });
                        store.createIndex("by-market", "marketId");
                    }
                },
                // Arrow function captures `this` from the enclosing method —
                // no aliasing needed.
                terminated: () => this.resetConnection(),
            });
        } finally {
            this.initPromise = null;
            this.gate.open();
        }
    }

    /**
     * Runs `operation` against the open DB.
     * On a connection error it closes the current handle, waits briefly,
     * then re-opens and retries — transparent to the caller.
     */
    private async executeWithRetry<T>(
        operation: (db: IDBPDatabase<DatabaseSchema>) => Promise<T>,
        retries = 2,
    ): Promise<T> {
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                await this.init();
                if (!this.db) throw new Error("Database not initialized");

                return await operation(this.db);
            } catch (error) {
                const isLastAttempt = attempt === retries;

                if (isIdbConnectionError(error) && !isLastAttempt) {
                    console.warn(
                        `IDB connection error on attempt ${
                            attempt + 1
                        }, reconnecting…`,
                        error,
                    );
                    await this.close();
                    // Small back-off: 50 ms on first retry, 150 ms on second.
                    await new Promise((resolve) =>
                        setTimeout(resolve, 50 * (attempt + 1) * 3),
                    );
                    continue;
                }

                throw error;
            }
        }

        throw new Error("Failed to execute database operation after retries");
    }

    // ---------------------------------------------------------------------------
    // Private utils
    // ---------------------------------------------------------------------------

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });
    }

    private base64ToBlob(base64: string): Blob {
        const arr = base64.split(",");
        const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    }

    // ---------------------------------------------------------------------------
    // Photos store
    // ---------------------------------------------------------------------------

    async savePhoto({
        id,
        file,
        fromSlot,
        skipUpload,
    }: {
        id: string;
        file: File;
        date: string;
        fromSlot: boolean;
        skipUpload?: boolean;
    }): Promise<void> {
        const base64 = await this.fileToBase64(file);

        return this.executeWithRetry(async (db) => {
            await db.put(PHOTO_STORE, {
                id,
                base64,
                name: file.name,
                type: file.type,
                size: file.size,
                taskComplete: false,
                uploadAttempts: 0,
                uploaded: false,
                fromSlot,
                skipUpload,
                createdAt: dayjs().toISOString(),
            });
        });
    }

    async getPhoto(id: string): Promise<string | null> {
        return this.executeWithRetry(async (db) => {
            const photo = await db.get(PHOTO_STORE, id);
            return photo?.base64 || null;
        });
    }

    async getAllPhotos(): Promise<StoredPhoto[]> {
        return this.executeWithRetry(async (db) => {
            return await db.getAll(PHOTO_STORE);
        });
    }

    async getPhotosByIds(ids: string[]): Promise<StoredPhoto[]> {
        return this.executeWithRetry(async (db) => {
            const photos = await Promise.all(
                ids.map((id) => db.get(PHOTO_STORE, id)),
            );
            return photos.filter((photo): photo is StoredPhoto =>
                Boolean(photo),
            );
        });
    }

    async deletePhoto(id: string): Promise<void> {
        return this.executeWithRetry(async (db) => {
            await db.delete(PHOTO_STORE, id);
        });
    }

    async deleteAllPhotos(): Promise<void> {
        return this.executeWithRetry(async (db) => {
            await db.clear(PHOTO_STORE);
        });
    }

    async deletePhotosByIds(ids: string[]): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction(PHOTO_STORE, "readwrite");
            await Promise.all([
                ...ids.map((id) => tx.store.delete(id)),
                tx.done,
            ]);
        });
    }

    async getPhotosCount(): Promise<number> {
        return this.executeWithRetry(async (db) => {
            return await db.count(PHOTO_STORE);
        });
    }

    async photoExists(id: string): Promise<boolean> {
        return this.executeWithRetry(async (db) => {
            const photo = await db.get(PHOTO_STORE, id);
            return !!photo;
        });
    }

    async getPhotoInfo(id: string): Promise<StoredPhotoMeta | null> {
        return this.executeWithRetry(async (db) => {
            const photo = await db.get(PHOTO_STORE, id);
            if (!photo) return null;
            const { base64: _base64, ...info } = photo;
            return info;
        });
    }

    async updatePhotoInfo(
        id: string,
        patch: Partial<StoredPhotoMeta>,
    ): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction(PHOTO_STORE, "readwrite");
            const store = tx.store;

            const existing = await store.get(id);
            if (!existing) {
                throw new Error(`Photo with id ${id} not found`);
            }

            await store.put({ ...existing, ...patch });
            await tx.done;
        });
    }

    async getTotalSize(): Promise<number> {
        return this.executeWithRetry(async (db) => {
            const photos = await db.getAll(PHOTO_STORE);
            return photos.reduce((total, photo) => total + photo.size, 0);
        });
    }

    // ---------------------------------------------------------------------------
    // Slots store
    // ---------------------------------------------------------------------------

    async getSlotAndDelete(marketId: string): Promise<StoredPhotoSlot | null> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction(SLOTS_STORE, "readwrite");
            const store = tx.objectStore(SLOTS_STORE);
            const index = store.index("by-market");

            const cursor = await index.openCursor(marketId);

            if (!cursor) {
                await tx.done;
                return null;
            }

            const slot = cursor.value;
            await cursor.delete();
            await tx.done;

            return slot;
        });
    }

    async createSlot({ url }: { url: string }) {
        return this.executeWithRetry(async (db) => {
            const parseData = parseUploadedUrl(url);

            if (!parseData) return;

            const { marketId, middlePath, filename } = parseData;
            await db.put(SLOTS_STORE, { url, marketId, filename, middlePath });
        });
    }

    async deleteSlot(url: string): Promise<void> {
        return this.executeWithRetry(async (db) => {
            await db.delete(SLOTS_STORE, url);
        });
    }

    // ---------------------------------------------------------------------------
    // Connection management
    // ---------------------------------------------------------------------------

    async close(): Promise<void> {
        if (this.db) {
            try {
                this.db.close();
            } catch (error) {
                console.warn("Error closing database:", error);
            } finally {
                this.db = null;
                this.initPromise = null;
                this.gate.close();
            }
        }
    }

    openGate(): void {
        this.gate.open();
    }

    closeGate(): void {
        this.gate.close();
    }

    // ------------- Kind
    async getKind(): Promise<"idb"> {
        return "idb";
    }
}

export { PhotoStorageDb };
